// Real content sourced from Indranil Kargupta's resume (frontend mock layer)

export const profile = {
  name: "Indranil Kargupta",
  firstName: "Indranil",
  lastName: "Kargupta",
  role: "Agentic Engineer — Quality Engineering",
  tagline:
    "Seasoned QE professional specializing in end-to-end Quality Engineering, UX design, and product experience.",
  longTagline:
    "I build resilient test platforms, automate the unautomatable, and design quality into every layer of the product — from API contracts to pixel-level UX.",
  location: "Bangalore, India",
  availability: "Open to senior QE / SDET / Quality Architect roles",
  email: "indranil.kargupta@outlook.com",
  phone: "+91 8777629166",
  photo: "/ProfilePicture.jpg",
  socials: {
    linkedin: "https://linkedin.com/in/ikg",
    github: "https://github.com/IKG12345",
  },
  certifications: [
    "ISTQB CTFL v4.0",
    "Google UX Design Professional",
    "NPTEL Java — IIT Kharagpur",
  ],
};

export const stats = [
  { value: "5+", label: "Years in Quality Engineering" },
  { value: "205+", label: "Tests automated end-to-end" },
  { value: "82%", label: "Automation coverage achieved of 788 manual tests" },
  { value: "60%", label: "Faster regression cycles — from 2 days to 6 hours" },
];

export const navLinks = [
  { id: "about", label: "About" },
  { id: "experience", label: "Experience" },
  { id: "projects", label: "Projects" },
  { id: "skills", label: "Skills" },
  { id: "contact", label: "Contact" },
];

export const aboutParagraphs = [
  "I'm a Quality Engineering professional with 5+ years across manual testing, test automation, DevOps, Test Management and AI-driven QE — spanning Retail, Utilities, and Fintech. My work sits at the intersection of test automation, developer experience, and product UX — because quality is not a phase, it's a mindset.",
  "I've designed automation frameworks with Selenium, Playwright, Java, Python, Typescript, RestAssured, TestNG, and Cucumber; embedded shift-left practices into CI/CD on Jenkins and Azure DevOps; and working in JPMorganChase & Co. in a critical tech team enhancing Bankers experience in closing deals on behalf of the bank.",
  "When I'm not chasing a flaky test, I'm sketching interfaces, mentoring engineers, or writing about the craft of quality.",
];

export const principles = [
  {
    title: "Quality is a mindset",
    body: "Not a checkpoint. Build it into design, code, and culture — not a gate at the end.",
  },
  {
    title: "Automate the boring",
    body: "Humans should think. Machines should repeat. Free engineers to do the unrepeatable work.",
  },
  {
    title: "Design is testing",
    body: "A great UX prevents a thousand defects. I treat design reviews as the first test pass.",
  },
];

export const education = [
  {
    school: "West Bengal University of Technology",
    degree: "B.Tech, Electronics & Communication Engineering",
    period: "Aug 2016 — Jul 2020",
  },
];

export const experience = [
  {
    company: "JPMorgan Chase & Co.",
    role: "SWE II — Test & Automation",
    period: "Mar 2026 — Present",
    location: "Commercial & Investment Banking · GIB Experience Tech Platform",
    summary:
      "Sole SDET on the Banker Efficiency team — driving end-to-end testing and release confidence for a high-velocity suite of applications that automate core investment banking workflows in a globally distributed model.",
    highlights: [
      "Champion QE within Rapid Application Development (RAD) apps — enabling iterative releases in months instead of years without compromising regulatory compliance",
      "Architect and maintain robust automation strategies aligned to team OKRs; integrate test suites into CI/CD pipelines for high-speed releases",
      "Primary QA anchor — analyze user stories, establish acceptance criteria, and weave Spec-Driven Development (SDD) and agentic AI workflows into the QE process",
    ],
    stack: ["Java", "Selenium", "Playwright", "TypeScript", "Groovy", "Agentic AI", "CI/CD", "SDD"],
  },
  {
    company: "Tata Consultancy Services",
    role: "SDET — Client: National Gas",
    period: "Jul 2023 — Mar 2026",
    location: "Gas Transmission & Metering · Digital Transformation",
    summary:
      "Sole QA contributor owning end-to-end quality for multiple concurrent, high-impact projects across National Gas's digital transformation — spanning ServiceNow, SAP SuccessFactors, and IBM Maximo.",
    highlights: [
      "Owned the complete QA lifecycle for ServiceNow (ITSM, HRSD, IAM, JML) — designing and executing test strategies for standalone releases and complex integrations",
      "Automated 205+ critical test cases across platforms — reducing regression from 5 days to 2 days at 82% automation coverage",
      "Managed full STLC on Azure DevOps — authoring test plans, executing scripts, and driving defect triage with development teams",
      "Awarded 'On the Spot Award' for contributions to the QE engagement for the UK & EU Business Unit",
    ],
    stack: [
      "Java",
      "Selenium",
      "RestAssured",
      "Azure DevOps",
      "ServiceNow",
      "SAP SuccessFactors",
      "IBM Maximo",
    ],
  },
  {
    company: "Wipro Limited",
    role: "Quality Assurance Engineer — Client: Tesco PLC",
    period: "Dec 2020 — May 2023",
    location: "Retail · Oracle Fusion Financial QE",
    summary:
      "Part of a 5-member QE team ensuring end-to-end quality of Oracle Fusion modules (EPBCS, ARCS, TRCS, R2C) — from test planning to automation to CI integration.",
    highlights: [
      "Built a Maven-based UI automation framework using Java, Selenium, JUnit, and Cucumber — cut test-cycle time by 40% and lifted coverage by 25%",
      "Conducted Functional and Non-Functional testing across UI, API, performance, and security layers",
      "Engaged in backend testing with Postman & RestAssured; integrated automated tests with Jenkins CI/CD pipelines",
      "Awarded 'IDEAS Extraordinaire Award' for process excellence and exceptional value delivery",
    ],
    stack: [
      "Java",
      "Selenium",
      "JUnit",
      "Cucumber",
      "RestAssured",
      "Postman",
      "Jenkins",
      "Oracle Fusion",
    ],
  },
];

export const projects = [
  {
    id: "p01",
    title: "Agentic Test Authoring Platform",
    kind: "Internal Platform",
    year: "2024",
    summary:
      "An AI-assisted test-design tool that turns user stories into runnable Selenium + RestAssured scenarios. Reduced regression authoring effort by 62%.",
    role: "Tech Lead & Architect",
    stack: ["Java", "Spring Boot", "OpenAI", "Selenium", "Playwright"],
    metrics: [
      { k: "Authoring time", v: "-62%" },
      { k: "Coverage uplift", v: "+34%" },
    ],
  },
  {
    id: "p02",
    title: "Self-Healing UI Automation Suite",
    kind: "Framework",
    year: "2021",
    summary:
      "A locator-resilient Selenium framework that auto-recovers from minor DOM drift using a fallback strategy + ML-ranked candidates.",
    role: "Lead Engineer",
    stack: ["Java", "Selenium", "TestNG", "Python (ML)"],
    metrics: [
      { k: "Flake rate", v: "-71%" },
      { k: "Maintenance hrs/sprint", v: "-40%" },
    ],
  },
  {
    id: "p03",
    title: "Contract-First QE Pipeline",
    kind: "CI/CD",
    year: "2023",
    summary:
      "Embedded Pact contract testing into the deployment pipeline of a 40-microservice fintech platform. Caught integration drift before staging.",
    role: "QE Architect",
    stack: ["Pact", "Jenkins", "Kubernetes", "Java"],
    metrics: [
      { k: "Integration defects", v: "-47%" },
      { k: "Mean time to detect", v: "-58%" },
    ],
  },
  {
    id: "p04",
    title: "Performance Lab",
    kind: "Performance",
    year: "2020",
    summary:
      "Built a reusable performance-testing lab with JMeter + Gatling, parameterized profiles, and dashboards. Used across 6 teams.",
    role: "Performance Lead",
    stack: ["JMeter", "Gatling", "Grafana", "InfluxDB"],
    metrics: [
      { k: "Bottlenecks found pre-launch", v: "3 critical" },
      { k: "Adoption", v: "6 teams" },
    ],
  },
  {
    id: "p05",
    title: "QE UX Audit Toolkit",
    kind: "Toolkit",
    year: "2022",
    summary:
      "A lightweight checklist + Figma plugin combo that lets PMs and QEs audit UX consistency before code is written. Open-sourced internally.",
    role: "Designer & Engineer",
    stack: ["Figma API", "TypeScript", "Node"],
    metrics: [{ k: "UX defects post-launch", v: "-29%" }],
  },
  {
    id: "p06",
    title: "Quality Guild & Mentorship",
    kind: "Community",
    year: "2022 — Present",
    summary:
      "An internal community of 80+ QE engineers running bi-weekly craft sessions, code reviews, and a rotating QE-of-the-month spotlight.",
    role: "Founder & Facilitator",
    stack: ["Notion", "Slack", "Curiosity"],
    metrics: [
      { k: "Engineers mentored", v: "30+" },
      { k: "Sessions run", v: "60+" },
    ],
  },
];

// Skill marquee belt: 3 rows that auto-scroll. Skills sourced from resume.
export const skillBeltRows = [
  // Row 1 — Languages + core automation
  [
    "Java",
    "Python",
    "TypeScript",
    "Groovy",
    "SQL",
    "Selenium",
    "Playwright",
    "TestNG",
    "JUnit",
    "Cucumber",
    "Page Object Model",
  ],
  // Row 2 — API, performance, mobile + management
  [
    "RestAssured",
    "Postman",
    "Bruno",
    "JMeter",
    "Blazemeter",
    "Appium",
    "Jira",
    "Rally",
    "Confluence",
    "Azure DevOps",
  ],
  // Row 3 — DevOps, SCM, enterprise platforms
  [
    "Jenkins",
    "Jules",
    "Git",
    "BitBucket",
    "CI/CD Pipelines",
    "ServiceNow",
    "Oracle Fusion",
    "SAP SuccessFactors",
    "IBM Maximo",
    "Spec-Driven Development",
    "Agentic AI",
  ],
];
